import React from 'react';
import {
	StyleSheet,
	View,
} from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../text/Text';
import Switch from '../switch/Switch';

export const SettingsSection = ({
	title,
	children,
	style,
	contentStyle,
}) => {
	const styles = useThemedStyles(createStyles);
	const rows = getRows(children);

	return (
		<View style={[styles.section, style]}>
			{title && (
				<Text style={styles.sectionTitle}>
					{title}
				</Text>
			)}
			<View style={[styles.group, contentStyle]}>
				{
					rows.map((child, index) => {
						if (!React.isValidElement(child)) {
							return child;
						}

						return React.cloneElement(child, {
							isLast: index === rows.length - 1,
						});
					})
				}
			</View>
		</View>
	);
};

const getRows = children => React.Children.toArray(children).reduce((rows, child) => {
	if (!child) {
		return rows;
	}

	if (React.isValidElement(child) && child.type === React.Fragment) {
		return rows.concat(getRows(child.props.children));
	}

	return rows.concat(child);
}, []);

export const SettingsRow = ({
	label,
	description,
	children,
	isLast,
	required = false,
	disabled = false,
	style,
	labelStyle,
	contentStyle,
}) => {
	const styles = useThemedStyles(createStyles);

	return (
		<View style={[
			styles.row,
			isLast && styles.rowLast,
			disabled && styles.rowDisabled,
			style,
		]}>
			<View style={styles.rowLabel}>
				<Text style={[
					styles.rowTitle,
					disabled && styles.rowTitleDisabled,
					labelStyle,
				]}>
					{label}
					{required && <Text style={styles.required}> *</Text>}
				</Text>
				{description && (
					<Text style={[
						styles.rowDescription,
						disabled && styles.rowTitleDisabled,
					]}>
						{description}
					</Text>
				)}
			</View>
			<View style={[styles.rowContent, contentStyle]}>
				{children}
			</View>
		</View>
	);
};

export const SettingsSwitchRow = ({
	label,
	value,
	onValueChange,
	required = false,
	disabled = false,
	...rowProps
}) => (
	<SettingsRow
		label={label}
		required={required}
		disabled={disabled}
		{...rowProps}
	>
		<Switch
			value={value}
			onValueChange={onValueChange}
			disabled={disabled}
			accessibilityLabel={typeof label === 'string' ? label : undefined}
		/>
	</SettingsRow>
);

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	section: {
		marginTop: space.gutter,
	},
	sectionTitle: {
		...type.sectionTitle,
		marginBottom: space.sm,
		color: colors.text,
	},
	group: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
		borderWidth: 1,
		borderRadius: radius.card,
		overflow: 'hidden',
	},
	row: {
		minHeight: 56,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderBottomColor: colors.border,
		borderBottomWidth: StyleSheet.hairlineWidth,
	},
	rowLast: {
		borderBottomWidth: 0,
	},
	rowDisabled: {
		backgroundColor: colors.disabledBg,
	},
	rowLabel: {
		flex: 1,
		minWidth: 0,
		paddingRight: space.md,
	},
	rowTitle: {
		...type.rowTitle,
		color: colors.text,
	},
	rowTitleDisabled: {
		color: colors.disabledText,
	},
	rowDescription: {
		...type.caption,
		marginTop: 2,
		color: colors.textSecondary,
	},
	rowContent: {
		flexShrink: 0,
		alignItems: 'flex-end',
		justifyContent: 'center',
	},
	required: {
		color: colors.danger,
	},
});
